import React, { createContext, useContext, useState } from "react";
import { toast } from "react-toastify";

import { appSettings } from "../config/appConfig";
import { useUserContext } from "./UserContextProvider";
import { logOutPage } from "../components/utils/logOut.js";

const MyLinksContext = createContext();

export const MyLinksContextProvider = ({ children }) => {
  const { setIsLoading } = useUserContext();
  const baseUrl = appSettings.serverBaseUrl;

  const [myLinksProfileName, setMyLinksProfileName] = useState("");
  const [myLinksLinks, setMyLinksLinks] = useState([]);
  const [isMyLinksEnabled, setIsMyLinksEnabled] = useState("");

  const getMyLinksLink = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${baseUrl}/mylinks/get-mylinks-link`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const resp = await response.json();
      if ("isValidToken" in resp && !resp?.isValidToken) {
        logOutPage();
      }
      setMyLinksLinks(resp);
      return resp;
    } catch (err) {
      toast.error("Failed to get MyLinks links");
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const getMyLinksProfileName = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${baseUrl}/mylinks/get-mylinks-profile-name`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const resp = await response.json();
      if ("isValidToken" in resp && !resp?.isValidToken) {
        logOutPage();
      }
      setMyLinksProfileName(resp?.[0]?.profileName);
      setIsMyLinksEnabled(resp?.[0]?.isMyLinksEnable);
    } catch (err) {
      console.log("getMyLinksProfileName err", err);
      toast.error("Failed to get MyLinks profile name");
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const getMyLinksByProfileName = async (profileName) => {
    try {
      const response = await fetch(
        `${baseUrl}/mylinks/get-mylinks-by-profile-name?profileName=${profileName}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const resp = await response.json();
      if ("isValidToken" in resp && !resp?.isValidToken) {
        logOutPage();
      }
      return resp;
    } catch (err) {
      console.log("getMyLinksByProfileName err", err);
      toast.error("Failed to get MyLinks link");
    } finally {
      setIsLoading(false);
    }
  };

  const createMyLinksLink = async (myLinksLink) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${baseUrl}/mylinks/create-mylinks-link`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(myLinksLink),
      });

      const result = await response.json();
      if ("isValidToken" in result && !result?.isValidToken) {
        logOutPage();
      } else {
        if (result?.Error) {
          toast.error(result.Error);
        } else if (result?.message) {
          toast.success(result.message);
        }
      }
    } catch (err) {
      toast.error("Failed to create MyLinks link");
    } finally {
      setIsLoading(false);
    }
  };

  const removeMyLinksLink = async (webLink) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${baseUrl}/mylinks/delete-mylinks-link`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ webLink }),
      });

      const result = await response.json();

      if ("isValidToken" in result && !result?.isValidToken) {
        logOutPage();
      } else {
        if (result?.Error) {
          toast.error(result.Error);
        } else if (result?.message) {
          toast.success(result.message);
        }
        setIsLoading(false);
      }
    } catch (err) {
      console.log("removeMyLinksLink err", err);
      setIsLoading(false);
      toast.error("Failed to remove MyLinks link");
    } finally {
      setIsLoading(false);
    }
  };

  const enableDisableMyLink = async (isEnable) => {
    try {
      const response = await fetch(
        `${baseUrl}/mylinks/enable-disable-mylinks-link`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ isEnable }),
        }
      );

      const result = await response.json();

      if ("isValidToken" in result && !result?.isValidToken) {
        logOutPage();
      } else {
        if (result?.Error) {
          toast.error(result.Error);
          return false;
        } else if (result?.message) {
          toast.success(result.message);
          return true;
        }
      }
    } catch (err) {
      console.log("enableDisableMyLink err", err);
      toast.error("Failed to update MyLinks link");
      return false;
    }
  };

  const updateMyLinkProfilePicture = async (profilePicture) => {
    try {
      const response = await fetch(
        `${baseUrl}/mylinks/update-mylinks-profile-picture`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ profilePicture }),
        }
      );

      const result = await response.json();

      if ("isValidToken" in result && !result?.isValidToken) {
        logOutPage();
      } else {
        if (result?.Error) {
          toast.error(result.Error);
          return false;
        } else if (result?.message) {
          toast.success(result.message);
          return true;
        }
      }
    } catch (err) {
      console.log("updateMyLinkProfilePicture err", err);
      toast.error("Failed to update MyLinks link profile picture");
      return false;
    }
  };

  const updateMyLinkThemeColor = async (themeColor) => {
    try {
      const response = await fetch(
        `${baseUrl}/mylinks/update-mylinks-theme-color`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ themeColor }),
        }
      );

      const result = await response.json();

      if ("isValidToken" in result && !result?.isValidToken) {
        logOutPage();
      } else {
        if (result?.Error) {
          toast.error(result.Error);
          return false;
        } else if (result?.message) {
          toast.success(result.message);
          return true;
        }
      }
    } catch (err) {
      console.log("updateMyLinkThemeColor err", err);
      toast.error("Failed to update MyLinks link theme color");
      return false;
    }
  };

  return (
    <MyLinksContext.Provider
      value={{
        createMyLinksLink,
        getMyLinksLink,
        removeMyLinksLink,
        getMyLinksProfileName,
        getMyLinksByProfileName,
        myLinksProfileName,
        setMyLinksProfileName,
        myLinksLinks,
        setMyLinksLinks,
        isMyLinksEnabled,
        enableDisableMyLink,
        setIsMyLinksEnabled,
        updateMyLinkProfilePicture,
        updateMyLinkThemeColor,
      }}
    >
      {children}
    </MyLinksContext.Provider>
  );
};

export const useMyLinksContext = () => useContext(MyLinksContext);
